import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { AuthModal } from '../features/auth';

const LandingPage = () => {
  const navigate = useNavigate();
  const { user, profile, loading } = useAuth();
  const [, setScrolled] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);

  const featureCards = [
    {
      icon: '📊',
      title: 'Student Insights',
      description: 'Visualize student characteristics through intuitive aura patterns',
      gradient: 'from-lime-500 to-emerald-600',
      iconGradient: 'from-lime-400 to-emerald-600'
    },
    {
      icon: '🎯',
      title: 'Adaptive Teaching',
      description: 'Get real-time suggestions to optimize your teaching approach',
      gradient: 'from-emerald-500 to-green-600',
      iconGradient: 'from-emerald-400 to-green-600'
    },
    {
      icon: '💡',
      title: 'Interactive Feedback',
      description: 'Foster meaningful connections through continuous engagement',
      gradient: 'from-green-500 to-lime-600',
      iconGradient: 'from-green-400 to-lime-600'
    },
    {
      icon: '🤖',
      title: 'Data-Driven Decisions',
      description: 'AI-powered analytics to make informed adjustments to your teaching methods',
      gradient: 'from-lime-500 to-emerald-600',
      iconGradient: 'from-lime-400 to-emerald-600'
    },
    {
      icon: '📝',
      title: 'Question Banks',
      description: 'Design surveys with pre-built templates aligned with pedagogical best practices',
      gradient: 'from-emerald-500 to-green-600',
      iconGradient: 'from-emerald-400 to-green-600'
    },
    {
      icon: '🎓',
      title: 'Research-Backed',
      description: 'Evidence-based suggestions to enhance your teaching practices',
      gradient: 'from-green-500 to-lime-600',
      iconGradient: 'from-green-400 to-lime-600'
    }
  ];

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Redirect if already authenticated
  useEffect(() => {
    console.log('LandingPage useEffect - loading:', loading, 'user:', user?.id, 'profile:', profile);
    if (!loading && user && profile) {
      console.log('Redirecting user with type:', profile.userType);
      if (profile.userType === 'professor') {
        navigate('/dashboard', { replace: true });
      } else {
        navigate('/student-dashboard', { replace: true });
      }
    }
  }, [loading, user, profile, navigate]);

  // Show loading spinner while authentication is being determined
  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Don't render landing page if user is authenticated (prevent flash)
  if (user && profile) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Redirecting...</p>
        </div>
      </div>
    );
  }

  const handleLogin = () => {
    setShowAuthModal(true);
  };

  // Double the cards to create seamless loop
  const displayCards = [...featureCards, ...featureCards];

  return (
    <div className="min-h-screen bg-white">
      {/* Top Half with Gradient */}
      <div className="h-[60vh] relative overflow-hidden">
        {/* Animated Background */}
        <div className="absolute inset-0 animate-gradient bg-[length:400%_400%] bg-gradient-to-br from-lime-400 via-emerald-500 to-green-600 opacity-90" />
        
        {/* Floating Shapes */}
        <div className="absolute inset-0">
          <div className="absolute top-20 left-20 w-32 h-32 bg-emerald-300/30 rounded-full blur-xl animate-float" />
          <div className="absolute top-40 right-32 w-24 h-24 bg-lime-400/20 rounded-lg rotate-45 blur-lg animate-float" style={{ animationDelay: '1s' }} />
          <div className="absolute bottom-20 left-1/3 w-40 h-40 bg-green-400/25 rounded-full blur-xl animate-float" style={{ animationDelay: '2s' }} />
        </div>

        {/* Content */}
        <div className="relative z-10 h-full flex flex-col justify-center items-center">
          <div className="text-center max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-center mb-6">
              <div className="relative">
                <div className="absolute inset-0 animate-pulse-slow">
                  <div className="absolute inset-0 bg-white opacity-20 rounded-full blur-xl transform scale-150" />
                </div>
                <img
                  src="/synced-logo.png"
                  alt="SyncED Logo"
                  className="h-32 w-auto relative animate-float"
                />
              </div>
            </div>
            
            <h1 className="text-5xl md:text-6xl font-extrabold text-white mb-4 tracking-tight">
              <span className="block transform transition-transform hover:scale-105 duration-300">
                Transform Your
              </span>
              <span className="block text-lime-100 transform transition-transform hover:scale-105 duration-300">
                Classroom Experience
              </span>
            </h1>
            
            <p className="text-lg md:text-xl text-white max-w-2xl mx-auto leading-relaxed bg-emerald-600/20 backdrop-blur-sm rounded-full py-2 px-6 inline-block">
              Bridging educators with student needs through dynamic insights
            </p>
          </div>
        </div>
      </div>

      {/* Bottom Half with Features */}
      <div className="bg-white py-20 overflow-hidden">
        <div className="max-w-7xl mx-auto">
          <div className="relative">
            <div className="overflow-hidden py-8">
              <div className="flex animate-carousel">
                {displayCards.map((card, index) => (
                  <div 
                    key={index}
                    className="w-full md:w-1/3 px-6 flex-shrink-0"
                    style={{ flex: '0 0 33.333333%' }}
                  >
                    <div className="relative group">
                      <div className={`absolute -inset-0.5 bg-gradient-to-r ${card.gradient} rounded-2xl blur opacity-25 group-hover:opacity-100 transition duration-1000 group-hover:duration-200 animate-tilt`} />
                      <div className="relative p-6 bg-white ring-1 ring-gray-900/5 rounded-2xl leading-none flex items-top justify-start space-x-6">
                        <div className="space-y-4">
                          <div className={`bg-gradient-to-br ${card.iconGradient} w-12 h-12 rounded-full flex items-center justify-center`}>
                            <span className="text-2xl text-white">{card.icon}</span>
                          </div>
                          <h3 className="text-2xl font-bold text-gray-900">{card.title}</h3>
                          <p className="text-gray-600">{card.description}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Call to Action Section */}
          <div className="text-center mt-20">
            <div className="max-w-3xl mx-auto mb-8">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Ready to Transform Your Teaching?
              </h2>
              <p className="text-lg text-gray-600 mb-8">
                Join educators and students already using SyncED to create more engaging, responsive learning environments.
              </p>
            </div>
            
            <div className="flex justify-center">
              <button
                onClick={handleLogin}
                className="px-12 py-4 bg-gradient-to-r from-lime-500 via-emerald-500 to-green-600 text-white rounded-full hover:opacity-90 transition-all duration-300 transform hover:scale-105 font-semibold text-xl shadow-lg"
              >
                Get Started Today
              </button>
            </div>
            
          </div>
        </div>
      </div>

      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        initialUserType="student"
      />
    </div>
  );
};

export default LandingPage;